import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  ChevronLeft,
  UserPlus,
  Camera,
  CalendarIcon,
  Phone,
  Mail,
  User,
  Briefcase,
  Plus,
  Trash2,
  Clock,
  X,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useCreateStaff } from "@/hooks/useStaffCreate";
import { useStaffRoles } from "@/hooks/useStaffList";
import { useClinics } from "@/hooks/useClinics";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { AccessDenied } from "@/components/AccessDenied";
import { getApiErrorStatus } from "@/lib/api";

// --- Availability slot types ---

interface AvailabilitySlot {
  day: string;
  start_time: string;
  end_time: string;
  location: string;
  mode: string;
  clinic_id: string | null;
}

const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DAY_LABELS: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const DAY_SHORT_LABELS: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

// --- Professional info entry types ---

interface ExperienceEntry {
  institution_name: string;
  designation: string;
  location: string;
  start_date: string;
  end_date: string;
}

interface EducationEntry {
  institution_name: string;
  degree: string;
  location: string;
  start_year: string;
  end_year: string;
}

interface CertificationEntry {
  title: string;
  year: string;
  school_name: string;
  location: string;
  certificate_link: string;
}

// --- Constants ---

const CLINICAL_ROLES: readonly string[] = [
  "doctor",
  "nutritionist",
  "physiotherapist",
];

const GENDER_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "O", label: "Other" },
] as const;

// --- Zod Schema ---

const COUNTRY_CODES = [
  { value: "+91", label: "IN +91", flag: "\u{1F1EE}\u{1F1F3}" },
  { value: "+1", label: "US +1", flag: "\u{1F1FA}\u{1F1F8}" },
] as const;

const createStaffSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  countryCode: z.string().min(1, "Country code is required"),
  phoneNumber: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^\d+$/, "Phone number must contain only digits"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  gender: z.enum(["M", "F", "O"], { required_error: "Gender is required" }),
  dob: z.date({ required_error: "Date of birth is required" }),
  userType: z.string().min(1, "Staff role is required"),
  designation: z.string().min(1, "Designation is required"),
  experience: z
    .string()
    .min(1, "Experience is required")
    .regex(/^\d+$/, "Experience must be a number"),
  languages: z.string().optional(),
  about: z.string().optional(),
});

type CreateStaffFormValues = z.infer<typeof createStaffSchema>;

// --- Default blank entries ---

const BLANK_EXP: ExperienceEntry = {
  institution_name: "",
  designation: "",
  location: "",
  start_date: "",
  end_date: "",
};

const BLANK_EDU: EducationEntry = {
  institution_name: "",
  degree: "",
  location: "",
  start_year: "",
  end_year: "",
};

const BLANK_CERT: CertificationEntry = {
  title: "",
  year: "",
  school_name: "",
  location: "",
  certificate_link: "",
};

// --- Component ---

export const CreateStaffForm = () => {
  const navigate = useNavigate();
  const createMutation = useCreateStaff();
  const { hasPermission } = useAuthStore();
  const {
    data: staffRoles,
    isLoading: areStaffRolesLoading,
    error: staffRolesError,
    refetch: refetchStaffRoles,
  } = useStaffRoles();
  const { data: clinics } = useClinics();

  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [createdStaff, setCreatedStaff] = useState<{
    name: string;
  } | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Professional info array state (managed outside react-hook-form since they are arrays in meta)
  const [experiences, setExperiences] = useState<ExperienceEntry[]>([]);
  const [educations, setEducations] = useState<EducationEntry[]>([]);
  const [certifications, setCertifications] = useState<CertificationEntry[]>(
    []
  );

  // Inline add-form visibility and temporary entry state
  const [showExpForm, setShowExpForm] = useState(false);
  const [newExp, setNewExp] = useState<ExperienceEntry>({ ...BLANK_EXP });

  const [showEduForm, setShowEduForm] = useState(false);
  const [newEdu, setNewEdu] = useState<EducationEntry>({ ...BLANK_EDU });

  const [showCertForm, setShowCertForm] = useState(false);
  const [newCert, setNewCert] = useState<CertificationEntry>({ ...BLANK_CERT });

  // Working hours slot state
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [addingForDay, setAddingForDay] = useState("");
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [slotMode, setSlotMode] = useState("teleconsult");
  const [slotClinicId, setSlotClinicId] = useState<string | null>(null);
  const [slotError, setSlotError] = useState("");

  const form = useForm<CreateStaffFormValues>({
    resolver: zodResolver(createStaffSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      countryCode: "+91",
      phoneNumber: "",
      email: "",
      gender: undefined,
      dob: undefined,
      userType: undefined,
      designation: "",
      experience: "",
      languages: "",
      about: "",
    },
  });

  const watchedUserType = form.watch("userType");
  const isClinicalRole = CLINICAL_ROLES.includes(watchedUserType);

  // --- Photo handlers ---

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    setPhotoPreview(null);
  };

  // --- Experience handlers ---

  const handleSaveExp = () => {
    if (
      !newExp.institution_name.trim() ||
      !newExp.designation.trim() ||
      !newExp.location.trim()
    )
      return;
    setExperiences((prev) => [...prev, { ...newExp }]);
    setNewExp({ ...BLANK_EXP });
    setShowExpForm(false);
  };

  const handleRemoveExp = (index: number) => {
    setExperiences((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Education handlers ---

  const handleSaveEdu = () => {
    if (
      !newEdu.institution_name.trim() ||
      !newEdu.degree.trim() ||
      !newEdu.location.trim()
    )
      return;
    setEducations((prev) => [...prev, { ...newEdu }]);
    setNewEdu({ ...BLANK_EDU });
    setShowEduForm(false);
  };

  const handleRemoveEdu = (index: number) => {
    setEducations((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Certification handlers ---

  const handleSaveCert = () => {
    if (!newCert.title.trim()) return;
    setCertifications((prev) => [...prev, { ...newCert }]);
    setNewCert({ ...BLANK_CERT });
    setShowCertForm(false);
  };

  const handleRemoveCert = (index: number) => {
    setCertifications((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Working hours handlers ---

  const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + (minutes || 0);
  };

  const validateSlotOverlap = (
    day: string,
    startTime: string,
    endTime: string,
    mode: string,
    clinicId: string | null
  ): string | null => {
    const startMin = timeToMinutes(startTime);
    const endMin = timeToMinutes(endTime);

    if (startMin >= endMin) {
      return "End time must be after start time";
    }

    for (const existing of slots) {
      if (
        existing.day === day &&
        existing.mode === mode &&
        existing.clinic_id === clinicId
      ) {
        const existStart = timeToMinutes(existing.start_time);
        const existEnd = timeToMinutes(existing.end_time);
        if (startMin < existEnd && existStart < endMin) {
          return `Overlaps with existing ${DAY_LABELS[day]} slot (${existing.start_time} - ${existing.end_time})`;
        }
      }
    }
    return null;
  };

  const handleAddSlot = () => {
    setSlotError("");
    if (!addingForDay || !slotStart || !slotEnd) {
      setSlotError("Start time and end time are required");
      return;
    }
    if (slotMode === "clinic" && !slotClinicId) {
      setSlotError("Select a clinic");
      return;
    }
    const startFormatted =
      slotStart.length === 5 ? `${slotStart}:00` : slotStart;
    const endFormatted = slotEnd.length === 5 ? `${slotEnd}:00` : slotEnd;
    const clinicId = slotMode === "clinic" ? slotClinicId : null;

    const error = validateSlotOverlap(
      addingForDay,
      startFormatted,
      endFormatted,
      slotMode,
      clinicId
    );
    if (error) {
      setSlotError(error);
      return;
    }

    const clinicName =
      slotMode === "clinic" && clinicId
        ? (clinics?.find((c) => c.id === clinicId)?.name ?? "In-Person")
        : "Tele-Consultations";

    setSlots((prev) => [
      ...prev,
      {
        day: addingForDay,
        start_time: startFormatted,
        end_time: endFormatted,
        location: clinicName,
        mode: slotMode,
        clinic_id: clinicId,
      },
    ]);
    setAddingForDay("");
    setSlotStart("");
    setSlotEnd("");
    setSlotClinicId(null);
  };

  const handleRemoveSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Submit handler ---

  const handleSubmit = async (values: CreateStaffFormValues) => {
    if (!staffRoles?.some((role) => role.name === values.userType)) {
      form.setError("userType", {
        message: "Select a valid staff role from the available options.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("first_name", values.firstName);
    formData.append("last_name", values.lastName);
    formData.append(
      "phone_number",
      `${values.countryCode}${values.phoneNumber}`
    );
    // Email is optional here, but the backend rejects a blank string — omit it
    // entirely rather than sending "".
    if (values.email) {
      formData.append("email", values.email);
    }
    formData.append("user_type", values.userType);

    const profile = {
      gender: values.gender,
      experience: parseInt(values.experience),
      designation: values.designation,
      dob: format(values.dob, "yyyy-MM-dd"),
      meta: {
        languages_known: values.languages
          ? values.languages
              .split(",")
              .map((l) => l.trim())
              .filter(Boolean)
          : [],
        about: values.about || "",
        previous_experience: experiences,
        certifications: certifications,
        education: educations,
      },
    };
    formData.append("profile", JSON.stringify(profile));
    formData.append("slots", JSON.stringify(slots));

    if (profilePhoto) {
      formData.append("profile_pic", profilePhoto);
    }

    createMutation.mutate(formData, {
      onSuccess: (data) => {
        setCreatedStaff({
          name: `${data.user.first_name} ${data.user.last_name}`,
        });
        setSuccessOpen(true);
      },
      onError: (error: any) => {
        const fieldMap: Record<string, string> = {
          first_name: "firstName",
          last_name: "lastName",
          phone_number: "phoneNumber",
          email: "email",
          user_type: "userType",
          gender: "gender",
          dob: "dob",
          designation: "designation",
          experience: "experience",
        };

        // Map DRF field-specific errors to form errors, and collect everything
        // that has no matching field so it still reaches the user.
        const unmapped: string[] = [];
        const details = error?.details;
        if (details && typeof details === "object" && !Array.isArray(details)) {
          Object.entries(details).forEach(([field, messages]) => {
            const text = Array.isArray(messages)
              ? String(messages[0])
              : String(messages);
            const formField = fieldMap[field];
            if (formField) {
              form.setError(formField as any, { message: text });
            } else {
              unmapped.push(text);
            }
          });
        }

        setErrorMessage(
          error?.status === 403
            ? "Your permissions no longer allow staff creation."
            : unmapped.length
              ? unmapped.join("\n")
              : error?.message ||
                "Failed to create staff member. Please try again."
        );
        setErrorOpen(true);
      },
    });
  };

  // --- Success dialog handler ---

  const handleSuccessOk = () => {
    setSuccessOpen(false);
    setCreatedStaff(null);
    setProfilePhoto(null);
    setPhotoPreview(null);
    setExperiences([]);
    setEducations([]);
    setCertifications([]);
    setSlots([]);
    form.reset();
    navigate("/settings/staff/view-staff");
  };

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_STAFF)) {
    return (
      <AccessDenied description="You do not have permission to create staff members." />
    );
  }

  if (getApiErrorStatus(staffRolesError) === 403) {
    return (
      <AccessDenied description="Your current permissions do not allow access to the staff role catalog." />
    );
  }

  // --- Schedule tab: split slots into Telehealth / In Clinic views ---

  const sortSlots = (list: AvailabilitySlot[]) =>
    [...list].sort((a, b) => {
      const dayOrder = DAYS_OF_WEEK.indexOf(
        a.day as (typeof DAYS_OF_WEEK)[number]
      );
      const dayOrderB = DAYS_OF_WEEK.indexOf(
        b.day as (typeof DAYS_OF_WEEK)[number]
      );
      if (dayOrder !== dayOrderB) return dayOrder - dayOrderB;
      return a.start_time.localeCompare(b.start_time);
    });

  const hasClinicSlots = slots.some((s) => s.mode === "clinic");
  const hasClinics = (clinics?.length ?? 0) > 0 || hasClinicSlots;
  const teleconsultSlots = sortSlots(
    slots.filter((s) => s.mode === "teleconsult")
  );
  const clinicSlots = sortSlots(slots.filter((s) => s.mode === "clinic"));
  const knownClinicIds = new Set((clinics ?? []).map((c) => c.id));
  const orphanClinicIds = Array.from(
    new Set(
      clinicSlots
        .map((s) => s.clinic_id)
        .filter((id): id is string => !!id && !knownClinicIds.has(id))
    )
  );
  const clinicSlotGroups = [
    ...(clinics ?? []).map((c) => ({
      clinicId: c.id,
      clinicName: c.name,
      slots: clinicSlots.filter((s) => s.clinic_id === c.id),
    })),
    ...orphanClinicIds.map((id) => ({
      clinicId: id,
      clinicName: "Unknown clinic",
      slots: clinicSlots.filter((s) => s.clinic_id === id),
    })),
  ].filter((group) => group.slots.length > 0);

  const findSlotIndex = (slot: AvailabilitySlot) =>
    slots.findIndex(
      (s) =>
        s.day === slot.day &&
        s.start_time === slot.start_time &&
        s.end_time === slot.end_time &&
        s.mode === slot.mode &&
        s.clinic_id === slot.clinic_id
    );

  const openAddSlotForDay = (mode: string, day: string) => {
    setAddingForDay(day);
    setSlotStart("");
    setSlotEnd("");
    setSlotMode(mode);
    setSlotClinicId(null);
    setSlotError("");
  };

  return (
    <>
      {/* Main form card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/settings/staff/view-staff")}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add New Staff Member
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <Tabs defaultValue="profile">
                <TabsList>
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  {isClinicalRole && (
                    <TabsTrigger value="professional">
                      Professional Info
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="schedule">Schedule</TabsTrigger>
                </TabsList>

                {/* Profile */}
                <TabsContent value="profile" className="mt-4 space-y-6">
                  {/* Photo Upload */}
                  <div className="flex flex-col items-center gap-3">
                    <div
                      className="relative w-24 h-24 rounded-full bg-muted border-2 border-dashed border-border flex items-center justify-center cursor-pointer overflow-hidden hover:bg-muted/30 transition-colors"
                      onClick={() =>
                        document.getElementById("profile-photo-input")?.click()
                      }
                    >
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Profile preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-muted-foreground">
                          <Camera className="h-8 w-8" />
                          <span className="text-xs">Photo</span>
                        </div>
                      )}
                    </div>
                    <input
                      id="profile-photo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoChange}
                    />
                    {photoPreview && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleRemovePhoto}
                      >
                        Remove Photo
                      </Button>
                    )}
                  </div>

                  {/* Staff Role */}
                  <FormField
                    control={form.control}
                    name="userType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Staff Role *</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={
                            areStaffRolesLoading ||
                            Boolean(staffRolesError) ||
                            !staffRoles?.length
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={
                                  areStaffRolesLoading
                                    ? "Loading staff roles..."
                                    : "Select staff role"
                                }
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {staffRoles?.map((role) => (
                              <SelectItem key={role.name} value={role.name}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {staffRolesError ? (
                          <div className="flex items-center gap-2 text-sm text-destructive">
                            <span>Unable to load staff roles.</span>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-destructive"
                              onClick={() => refetchStaffRoles()}
                            >
                              Retry
                            </Button>
                          </div>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Personal Information */}
                  <div>
                    <h3 className="z-h3 text-foreground mb-4">
                      Personal Information
                    </h3>
                    <div className="space-y-4">
                      {/* First Name & Last Name */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                First Name *
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter first name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Last Name *
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter last name"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Phone Number & Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="phoneNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Phone Number *
                              </FormLabel>
                              <FormControl>
                                <div className="flex gap-2">
                                  <FormField
                                    control={form.control}
                                    name="countryCode"
                                    render={({ field: ccField }) => (
                                      <Select
                                        value={ccField.value}
                                        onValueChange={ccField.onChange}
                                      >
                                        <SelectTrigger className="w-32">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {COUNTRY_CODES.map((cc) => (
                                            <SelectItem
                                              key={cc.value}
                                              value={cc.value}
                                            >
                                              {cc.flag} {cc.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                  />
                                  <Input
                                    placeholder="Enter phone number"
                                    {...field}
                                    className="flex-1"
                                  />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email (Optional)
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="Enter email address"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Gender & DOB */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Gender *</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select gender" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {GENDER_OPTIONS.map((option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="dob"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Date of Birth *</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full justify-start text-left font-normal",
                                        !field.value && "text-muted-foreground"
                                      )}
                                    >
                                      <CalendarIcon className="mr-2 h-4 w-4" />
                                      {field.value ? (
                                        format(field.value, "PPP")
                                      ) : (
                                        <span>Pick a date</span>
                                      )}
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="single"
                                    selected={field.value}
                                    onSelect={field.onChange}
                                    disabled={(date) => {
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      return date > today;
                                    }}
                                    initialFocus
                                    defaultMonth={new Date(1990, 0)}
                                    captionLayout="dropdown"
                                    startMonth={new Date(1940, 0)}
                                    endMonth={
                                      new Date(new Date().getFullYear(), 11)
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Designation & Experience */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="designation"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Briefcase className="h-4 w-4" />
                                Designation *
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g. Senior Cardiologist"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="experience"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Experience (years) *</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. 5" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Languages */}
                      <FormField
                        control={form.control}
                        name="languages"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Languages Known (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g. English, Hindi, Tamil"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* About */}
                      <FormField
                        control={form.control}
                        name="about"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>About (Optional)</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Brief description about the staff member..."
                                className="resize-none"
                                rows={3}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Professional Info — only for clinical roles */}
                {isClinicalRole && (
                  <TabsContent value="professional" className="mt-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle>Professional Information</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Experience sub-section */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="z-body-md text-foreground">
                              Experience
                            </h4>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setNewExp({ ...BLANK_EXP });
                                setShowExpForm(true);
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>

                          {/* Experience entries list */}
                          {experiences.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {experiences.map((exp, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-card border rounded-lg"
                                >
                                  <div className="text-sm">
                                    <span className="font-medium">
                                      {exp.designation}
                                    </span>{" "}
                                    at{" "}
                                    <span className="font-medium">
                                      {exp.institution_name}
                                    </span>
                                    {(exp.start_date || exp.end_date) && (
                                      <span className="text-muted-foreground ml-1">
                                        ({exp.start_date}
                                        {exp.end_date
                                          ? ` - ${exp.end_date}`
                                          : ""}
                                        )
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleRemoveExp(index)}
                                    className="h-7 w-7 flex-shrink-0"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Inline add experience form */}
                          {showExpForm && (
                            <div className="bg-muted rounded-lg p-4 border space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Organization Name *
                                  </label>
                                  <Input
                                    placeholder="e.g. Apollo Hospitals"
                                    value={newExp.institution_name}
                                    onChange={(e) =>
                                      setNewExp((prev) => ({
                                        ...prev,
                                        institution_name: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Designation *
                                  </label>
                                  <Input
                                    placeholder="e.g. Senior Doctor"
                                    value={newExp.designation}
                                    onChange={(e) =>
                                      setNewExp((prev) => ({
                                        ...prev,
                                        designation: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Location *
                                  </label>
                                  <Input
                                    placeholder="e.g. Mumbai"
                                    value={newExp.location}
                                    onChange={(e) =>
                                      setNewExp((prev) => ({
                                        ...prev,
                                        location: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                      Start Date
                                    </label>
                                    <Input
                                      placeholder="e.g. Jan 2020"
                                      value={newExp.start_date}
                                      onChange={(e) =>
                                        setNewExp((prev) => ({
                                          ...prev,
                                          start_date: e.target.value,
                                        }))
                                      }
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                      End Date
                                    </label>
                                    <Input
                                      placeholder="e.g. Dec 2023"
                                      value={newExp.end_date}
                                      onChange={(e) =>
                                        setNewExp((prev) => ({
                                          ...prev,
                                          end_date: e.target.value,
                                        }))
                                      }
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setShowExpForm(false);
                                    setNewExp({ ...BLANK_EXP });
                                  }}
                                  className="h-7 px-3 text-xs"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={handleSaveExp}
                                  disabled={
                                    !newExp.institution_name.trim() ||
                                    !newExp.designation.trim() ||
                                    !newExp.location.trim()
                                  }
                                  className="h-7 px-3 text-xs"
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Education sub-section */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="z-body-md text-foreground">
                              Education
                            </h4>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setNewEdu({ ...BLANK_EDU });
                                setShowEduForm(true);
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>

                          {/* Education entries list */}
                          {educations.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {educations.map((edu, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-card border rounded-lg"
                                >
                                  <div className="text-sm">
                                    <span className="font-medium">
                                      {edu.degree}
                                    </span>{" "}
                                    at{" "}
                                    <span className="font-medium">
                                      {edu.institution_name}
                                    </span>
                                    {(edu.start_year || edu.end_year) && (
                                      <span className="text-muted-foreground ml-1">
                                        ({edu.start_year}
                                        {edu.end_year
                                          ? ` - ${edu.end_year}`
                                          : ""}
                                        )
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleRemoveEdu(index)}
                                    className="h-7 w-7 flex-shrink-0"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Inline add education form */}
                          {showEduForm && (
                            <div className="bg-muted rounded-lg p-4 border space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Institution Name *
                                  </label>
                                  <Input
                                    placeholder="e.g. AIIMS Delhi"
                                    value={newEdu.institution_name}
                                    onChange={(e) =>
                                      setNewEdu((prev) => ({
                                        ...prev,
                                        institution_name: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Degree *
                                  </label>
                                  <Input
                                    placeholder="e.g. MBBS"
                                    value={newEdu.degree}
                                    onChange={(e) =>
                                      setNewEdu((prev) => ({
                                        ...prev,
                                        degree: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Location *
                                  </label>
                                  <Input
                                    placeholder="e.g. New Delhi"
                                    value={newEdu.location}
                                    onChange={(e) =>
                                      setNewEdu((prev) => ({
                                        ...prev,
                                        location: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                      Start Year
                                    </label>
                                    <Input
                                      placeholder="e.g. 2018"
                                      value={newEdu.start_year}
                                      onChange={(e) =>
                                        setNewEdu((prev) => ({
                                          ...prev,
                                          start_year: e.target.value,
                                        }))
                                      }
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                      End Year
                                    </label>
                                    <Input
                                      placeholder="e.g. 2024"
                                      value={newEdu.end_year}
                                      onChange={(e) =>
                                        setNewEdu((prev) => ({
                                          ...prev,
                                          end_year: e.target.value,
                                        }))
                                      }
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setShowEduForm(false);
                                    setNewEdu({ ...BLANK_EDU });
                                  }}
                                  className="h-7 px-3 text-xs"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={handleSaveEdu}
                                  disabled={
                                    !newEdu.institution_name.trim() ||
                                    !newEdu.degree.trim() ||
                                    !newEdu.location.trim()
                                  }
                                  className="h-7 px-3 text-xs"
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Certifications sub-section */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="z-body-md text-foreground">
                              Certifications
                            </h4>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setNewCert({ ...BLANK_CERT });
                                setShowCertForm(true);
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>

                          {/* Certification entries list */}
                          {certifications.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {certifications.map((cert, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-card border rounded-lg"
                                >
                                  <div className="text-sm">
                                    <span className="font-medium">
                                      {cert.title}
                                    </span>
                                    {cert.school_name && (
                                      <>
                                        {" "}
                                        &mdash;{" "}
                                        <span className="font-medium">
                                          {cert.school_name}
                                        </span>
                                      </>
                                    )}
                                    {cert.year && (
                                      <span className="text-muted-foreground ml-1">
                                        ({cert.year})
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleRemoveCert(index)}
                                    className="h-7 w-7 flex-shrink-0"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Inline add certification form */}
                          {showCertForm && (
                            <div className="bg-muted rounded-lg p-4 border space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Title *
                                  </label>
                                  <Input
                                    placeholder="e.g. ACLS Certification"
                                    value={newCert.title}
                                    onChange={(e) =>
                                      setNewCert((prev) => ({
                                        ...prev,
                                        title: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Year
                                  </label>
                                  <Input
                                    placeholder="e.g. 2024"
                                    value={newCert.year}
                                    onChange={(e) =>
                                      setNewCert((prev) => ({
                                        ...prev,
                                        year: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Organization
                                  </label>
                                  <Input
                                    placeholder="e.g. American Heart Association"
                                    value={newCert.school_name}
                                    onChange={(e) =>
                                      setNewCert((prev) => ({
                                        ...prev,
                                        school_name: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Location
                                  </label>
                                  <Input
                                    placeholder="e.g. Online"
                                    value={newCert.location}
                                    onChange={(e) =>
                                      setNewCert((prev) => ({
                                        ...prev,
                                        location: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                    Certificate Link (Optional)
                                  </label>
                                  <Input
                                    placeholder="e.g. https://..."
                                    value={newCert.certificate_link}
                                    onChange={(e) =>
                                      setNewCert((prev) => ({
                                        ...prev,
                                        certificate_link: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-sm"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setShowCertForm(false);
                                    setNewCert({ ...BLANK_CERT });
                                  }}
                                  className="h-7 px-3 text-xs"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={handleSaveCert}
                                  disabled={!newCert.title.trim()}
                                  className="h-7 px-3 text-xs"
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {/* Schedule */}
                <TabsContent value="schedule" className="mt-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Schedule
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Tabs defaultValue="telehealth">
                        <TabsList>
                          <TabsTrigger value="telehealth">
                            Telehealth
                          </TabsTrigger>
                          {hasClinics && (
                            <TabsTrigger value="clinic">In Clinic</TabsTrigger>
                          )}
                        </TabsList>

                        {/* Telehealth — weekly column view */}
                        <TabsContent
                          value="telehealth"
                          className="mt-4 space-y-1"
                        >
                          {DAYS_OF_WEEK.map((day) => {
                            const daySlots = teleconsultSlots.filter(
                              (s) => s.day === day
                            );
                            const isAdding =
                              addingForDay === day &&
                              slotMode === "teleconsult";

                            return (
                              <div
                                key={day}
                                className="border rounded-lg overflow-hidden"
                              >
                                <div className="flex items-center p-3 gap-3">
                                  <span className="w-12 shrink-0 text-sm font-medium">
                                    {DAY_SHORT_LABELS[day]}
                                  </span>

                                  {daySlots.length === 0 ? (
                                    <span className="text-sm text-muted-foreground flex-1">
                                      Unavailable
                                    </span>
                                  ) : (
                                    <div className="flex flex-wrap gap-1.5 flex-1">
                                      {daySlots.map((slot, i) => {
                                        const idx = findSlotIndex(slot);
                                        return (
                                          <div
                                            key={i}
                                            className="flex items-center gap-1.5 bg-muted rounded-md px-2.5 py-1"
                                          >
                                            <span className="text-sm">
                                              {slot.start_time.slice(0, 5)} –{" "}
                                              {slot.end_time.slice(0, 5)}
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveSlot(idx)
                                              }
                                              className="text-muted-foreground hover:text-destructive transition-colors"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      openAddSlotForDay("teleconsult", day)
                                    }
                                    className="h-7 w-7 shrink-0"
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </Button>
                                </div>

                                {isAdding && (
                                  <div className="border-t bg-muted/50 p-3 space-y-2">
                                    <div className="flex items-end gap-2">
                                      <div className="flex-1">
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                          Start Time
                                        </label>
                                        <Input
                                          type="time"
                                          value={slotStart}
                                          onChange={(e) =>
                                            setSlotStart(e.target.value)
                                          }
                                          className="h-8 text-sm"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                          End Time
                                        </label>
                                        <Input
                                          type="time"
                                          value={slotEnd}
                                          onChange={(e) =>
                                            setSlotEnd(e.target.value)
                                          }
                                          className="h-8 text-sm"
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleAddSlot}
                                        className="h-8"
                                      >
                                        Add
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setAddingForDay("");
                                          setSlotError("");
                                        }}
                                        className="h-8 px-2"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    {slotError && (
                                      <p className="text-xs text-destructive">
                                        {slotError}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </TabsContent>

                        {/* In Clinic — weekly column view */}
                        {hasClinics && (
                          <TabsContent
                            value="clinic"
                            className="mt-4 space-y-1"
                          >
                            {DAYS_OF_WEEK.map((day) => {
                              const daySlots = clinicSlots.filter(
                                (s) => s.day === day
                              );
                              const isAdding =
                                addingForDay === day && slotMode === "clinic";

                              return (
                                <div
                                  key={day}
                                  className="border rounded-lg overflow-hidden"
                                >
                                  <div className="flex items-center p-3 gap-3">
                                    <span className="w-12 shrink-0 text-sm font-medium">
                                      {DAY_SHORT_LABELS[day]}
                                    </span>

                                    {daySlots.length === 0 ? (
                                      <span className="text-sm text-muted-foreground flex-1">
                                        Unavailable
                                      </span>
                                    ) : (
                                      <div className="flex flex-wrap gap-1.5 flex-1">
                                        {daySlots.map((slot, i) => {
                                          const idx = findSlotIndex(slot);
                                          return (
                                            <div
                                              key={i}
                                              className="flex items-center gap-1.5 bg-muted rounded-md px-2.5 py-1"
                                            >
                                              <span className="text-sm">
                                                {slot.start_time.slice(0, 5)} –{" "}
                                                {slot.end_time.slice(0, 5)}
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                {slot.location}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleRemoveSlot(idx)
                                                }
                                                className="text-muted-foreground hover:text-destructive transition-colors"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        openAddSlotForDay("clinic", day)
                                      }
                                      className="h-7 w-7 shrink-0"
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>

                                  {isAdding && (
                                    <div className="border-t bg-muted/50 p-3 space-y-2">
                                      <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                          Clinic
                                        </label>
                                        <select
                                          value={slotClinicId ?? ""}
                                          onChange={(e) =>
                                            setSlotClinicId(
                                              e.target.value || null
                                            )
                                          }
                                          className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                        >
                                          <option value="">
                                            Select clinic
                                          </option>
                                          {clinics?.map((clinic) => (
                                            <option
                                              key={clinic.id}
                                              value={clinic.id}
                                            >
                                              {clinic.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="flex items-end gap-2">
                                        <div className="flex-1">
                                          <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                            Start Time
                                          </label>
                                          <Input
                                            type="time"
                                            value={slotStart}
                                            onChange={(e) =>
                                              setSlotStart(e.target.value)
                                            }
                                            className="h-8 text-sm"
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                            End Time
                                          </label>
                                          <Input
                                            type="time"
                                            value={slotEnd}
                                            onChange={(e) =>
                                              setSlotEnd(e.target.value)
                                            }
                                            className="h-8 text-sm"
                                          />
                                        </div>
                                        <Button
                                          type="button"
                                          size="sm"
                                          onClick={handleAddSlot}
                                          className="h-8"
                                        >
                                          Add
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setAddingForDay("");
                                            setSlotError("");
                                          }}
                                          className="h-8 px-2"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      {slotError && (
                                        <p className="text-xs text-destructive">
                                          {slotError}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </TabsContent>
                        )}
                      </Tabs>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Submit */}
              <div className="pt-2 flex justify-end">
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Create Staff Member
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Success Dialog */}
      <AlertDialog open={successOpen} onOpenChange={setSuccessOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Staff Created Successfully!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-foreground">
                {createdStaff && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      The staff member has been created and can now access the
                      system.
                    </p>
                    <div className="bg-muted/30 p-4 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          Name:
                        </span>
                        <span className="text-sm font-semibold text-foreground">
                          {createdStaff.name}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleSuccessOk}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error Dialog — surfaces backend message when creation fails */}
      <AlertDialog open={errorOpen} onOpenChange={setErrorOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Failed to Create Staff
            </AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CreateStaffForm;
